import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft,
  Download,
  FileText,
  TrendingUp,
  TrendingDown,
  Minus,
  Loader2,
  AlertCircle,
  Calendar,
  Building2,
  Globe,
  ShieldCheck,
  AlertTriangle
} from 'lucide-react';
import { intelligenceAPI } from '../services/api';
import StyledMarkdown from '../components/StyledMarkdown';

const ReportDetailPage = () => {
  const { market, symbol } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  useEffect(() => {
    const fetchReport = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await intelligenceAPI.getReport(market, symbol);
        setReport(data);
      } catch (err) {
        console.error('Error fetching report:', err);
        setError('Erreur lors du chargement du rapport');
      } finally {
        setLoading(false);
      }
    };

    if (market && symbol) {
      fetchReport();
    }
  }, [market, symbol]);

  const handleDownloadPDF = async () => {
    setDownloadingPDF(true);
    try {
      const blob = await intelligenceAPI.downloadPDF(market, symbol);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport-${symbol}-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      alert('Erreur lors du téléchargement du PDF');
    } finally {
      setDownloadingPDF(false);
    }
  };

  const getRecommendationStyle = (recommendation) => {
    const styles = {
      'BUY': 'bg-[#00ff88]/20 text-[#00ff88] border border-[#00ff88]/30',
      'STRONG BUY': 'bg-[#00ff88]/30 text-[#00ff88] border border-[#00ff88]/40',
      'HOLD': 'bg-[#ffa502]/20 text-[#ffa502] border border-[#ffa502]/30',
      'SELL': 'bg-[#ff4757]/20 text-[#ff4757] border border-[#ff4757]/30',
      'STRONG SELL': 'bg-[#ff4757]/30 text-[#ff4757] border border-[#ff4757]/40'
    };
    return styles[recommendation] || 'bg-gray-500/20 text-gray-400';
  };

  if (loading) {
    return (
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 flex items-center justify-center min-h-screen">
        <Loader2 className="w-10 h-10 text-[#00ff88] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-red-500/10 border-red-500/20">
            <CardContent className="p-10 text-center">
              <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
              <p className="text-red-400 text-lg">{error}</p>
              <Link to="/reports">
                <Button className="mt-4" variant="outline">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Retour aux rapports
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Link to="/reports" className="flex items-center text-gray-400 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour aux rapports
          </Link>
          <Button
            onClick={handleDownloadPDF}
            disabled={downloadingPDF}
            className="bg-emerald-500 hover:bg-emerald-600 text-white"
          >
            {downloadingPDF ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Télécharger PDF
          </Button>
        </div>

        {/* Report Header Card */}
        <Card className="bg-[#1a1a2e] border border-[#2a2a3e] rounded-xl mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-white mb-2">
                  {report?.name || symbol}
                </h1>
                <div className="flex items-center gap-3 text-gray-400 text-sm flex-wrap">
                  <Badge variant="outline" className="border-[#2a2a3e] bg-[#0f0f23]">
                    {symbol}
                  </Badge>
                  <span className="flex items-center gap-1">
                    <Globe className="w-4 h-4 text-[#00ff88]" />
                    {report?.country || market}
                  </span>
                  <span className="flex items-center gap-1">
                    <Building2 className="w-4 h-4 text-[#00ff88]" />
                    {report?.sector}
                  </span>
                  {report?.generated_at && (
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4 text-[#00ff88]" />
                      {new Date(report.generated_at).toLocaleDateString('fr-FR')}
                    </span>
                  )}
                </div>
              </div>
              
              {report?.recommendation && (
                <Badge className={`${getRecommendationStyle(report.recommendation)} text-lg px-4 py-2`}>
                  {report.recommendation === 'BUY' || report.recommendation === 'STRONG BUY' ? (
                    <TrendingUp className="w-5 h-5 mr-2" />
                  ) : report.recommendation === 'SELL' || report.recommendation === 'STRONG SELL' ? (
                    <TrendingDown className="w-5 h-5 mr-2" />
                  ) : (
                    <Minus className="w-5 h-5 mr-2" />
                  )}
                  {report.recommendation}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Quality Scores */}
        {report?.quality_scores && (
          <Card className="bg-[#1a1a2e] border border-[#2a2a3e] rounded-xl mb-6">
            <CardHeader className="border-b border-[#2a2a3e]">
              <CardTitle className="text-white flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-[#00ff88]" />
                Scores de Qualité
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 rounded-lg bg-[#0f0f23] border border-[#2a2a3e]">
                  <p className="text-[#4a4a5e] text-sm mb-1">Beneish M-Score</p>
                  <p className={`text-2xl font-bold ${
                    report.quality_scores.beneish_score == null ? 'text-[#4a4a5e]' :
                    report.quality_scores.beneish_score < -2.22 ? 'text-[#00ff88]' : 'text-[#ff4757]'
                  }`}>
                    {report.quality_scores.beneish_score == null ? '—' : report.quality_scores.beneish_score.toFixed(2)}
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-[#0f0f23] border border-[#2a2a3e]">
                  <p className="text-[#4a4a5e] text-sm mb-1">Altman Z''-Score</p>
                  <p className={`text-2xl font-bold ${
                    report.quality_scores.altman_score == null ? 'text-[#4a4a5e]' :
                    report.quality_scores.altman_score >= 2.6 ? 'text-[#00ff88]' :
                    report.quality_scores.altman_score >= 1.1 ? 'text-[#ffa502]' : 'text-[#ff4757]'
                  }`}>
                    {report.quality_scores.altman_score == null ? '—' : report.quality_scores.altman_score.toFixed(2)}
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-[#0f0f23] border border-[#2a2a3e]">
                  <p className="text-[#4a4a5e] text-sm mb-1">Piotroski F-Score</p>
                  <p className={`text-2xl font-bold ${
                    report.quality_scores.piotroski_score == null ? 'text-[#4a4a5e]' :
                    report.quality_scores.piotroski_score >= 7 ? 'text-[#00ff88]' :
                    report.quality_scores.piotroski_score >= 4 ? 'text-[#ffa502]' : 'text-[#ff4757]'
                  }`}>
                    {report.quality_scores.piotroski_score == null ? '—' : `${report.quality_scores.piotroski_score}/9`}
                  </p>
                </div>
              </div>
              
              {report.quality_scores.red_flags?.length > 0 && (
                <div className="mt-4 p-4 rounded-lg bg-[#ff4757]/10 border border-[#ff4757]/30">
                  <div className="flex items-center gap-2 text-[#ff4757] mb-2">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="font-medium">Red Flags</span>
                  </div>
                  <ul className="space-y-1">
                    {report.quality_scores.red_flags.map((flag, i) => (
                      <li key={i} className="flex items-start gap-2 text-[#ff4757]/80 text-sm">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#ff4757] flex-shrink-0" />
                        {flag}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Report Content */}
        <Card className="bg-[#1a1a2e] border border-[#2a2a3e] rounded-xl">
          <CardHeader className="border-b border-[#2a2a3e]">
            <CardTitle className="text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#00ff88]" />
              Analyse Complète
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {report?.report_markdown || report?.content || report?.markdown_content ? (
              <StyledMarkdown 
                content={report.report_markdown || report.content || report.markdown_content}
              />
            ) : (
              <div className="text-center py-10 text-gray-400">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Contenu du rapport non disponible</p>
                <p className="text-sm mt-2">Le rapport peut être téléchargé en PDF</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ReportDetailPage;
